import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FaUsers, FaCheckCircle, FaTimesCircle, FaSpinner, FaClock } from 'react-icons/fa';
import { MemberWelcomeTour } from '../components/MemberWelcomeTour';

const API = '/api';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [familyId, setFamilyId] = useState(null);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  useEffect(() => {
    if (token) {
      verifyInvitation();
    } else {
      setError('No invitation token provided');
      setLoading(false);
    }
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await axios.get(`${API}/invitations/verify/${token}`);
      setInvitation(response.data);
      setFormData(prev => ({ ...prev, name: response.data.proxy_name }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/invitations/accept`, {
        token,
        name: formData.name,
        password: formData.password
      });
      
      // Auto login with the session token
      localStorage.setItem('session_token', response.data.session_token);
      
      // Check if pending approval
      if (response.data.status === 'pending_approval') {
        setPendingApproval(true);
        setFamilyName(response.data.family_name);
      } else {
        setSuccess(true);
        setFamilyId(response.data.family_id);
        setFamilyName(response.data.family_name || 'your family');
        // Show the member welcome tour
        setShowWelcomeTour(true);
      }
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-4xl text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  // Pending approval state
  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaClock className="text-4xl text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Account Created!</h1>
          <p className="text-gray-600 mb-4">
            Your account has been created successfully.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-800 font-medium mb-2">Awaiting Approval</p>
            <p className="text-sm text-amber-700">
              The owner of <strong>{familyName}</strong> needs to approve your membership before you can access the family.
            </p>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            You'll be able to access the family once approved. In the meantime, you can log in to check your status.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome to the Family!</h1>
            <p className="text-gray-600 mb-4">
              Your account has been created successfully. Let's show you around!
            </p>
            {!showWelcomeTour && (
              <button
                onClick={() => setShowWelcomeTour(true)}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
              >
                Start Tour
              </button>
            )}
          </div>
        </div>
        
        {/* Member Welcome Tour */}
        <MemberWelcomeTour 
          isOpen={showWelcomeTour}
          onClose={() => {
            setShowWelcomeTour(false);
            // Navigate to family page after tour
            if (familyId) {
              navigate(`/family/${familyId}`);
            }
          }}
          familyId={familyId}
        />
      </>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invitation Invalid</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaUsers className="text-3xl text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Join Your Family!</h1>
          <p className="text-gray-600">
            <strong>{invitation?.invited_by_name}</strong> has invited you to join the{' '}
            <strong>{invitation?.family_name}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-gray-700 font-medium mb-2">Your Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              required
              data-testid="invitation-name-input"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Create Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              placeholder="At least 6 characters"
              required
              data-testid="invitation-password-input"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Confirm Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              required
              data-testid="invitation-confirm-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            data-testid="invitation-submit-btn"
          >
            {creating ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create My Free Account</span>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              Log in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
